import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Linkedin,
  Instagram,
  Twitter,
  MapPin,
  Mail,
} from "lucide-react";
import { subscribeNewsletter } from "../../lib/api";
import { toast } from "sonner";

export default function FooterV2() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!email) return;
    setSending(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're in. Expect UAE Corporate Tax updates once a month.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? detail[0]?.msg : detail || "Couldn't subscribe."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <footer
      data-testid="v2-footer"
      className="relative bg-[#001438] text-white"
    >
      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#3366FF]/40 to-transparent" />
      <div className="container-x py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Link to="/" className="inline-flex items-center" aria-label="StartINC home">
            <img
              src="/brand/startinc-onnavy.png"
              alt="StartINC"
              className="h-12 w-auto select-none"
              draggable={false}
            />
          </Link>
          <p className="mt-5 text-slate-300/80 max-w-sm leading-relaxed">
            UAE business setup, accounting & auditing — delivered with the
            discipline of a private bank and the speed of a startup.
          </p>

          <form onSubmit={onSubscribe} className="mt-10 max-w-md" data-testid="v2-newsletter">
            <label className="overline text-white/60 block mb-3">
              Stay updated on UAE Corporate Tax
            </label>
            <div className="relative">
              <input
                data-testid="v2-newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@company.com"
                className="w-full rounded-sm bg-white/5 border border-white/20 text-white placeholder-white/40 pl-3 pr-14 py-3 focus:outline-none focus:border-[#6B8AFF]/60 focus:ring-2 focus:ring-[#6B8AFF]/20"
                required
              />
              <button
                data-testid="v2-newsletter-submit"
                type="submit"
                disabled={sending}
                className="absolute right-1.5 top-1.5 bottom-1.5 px-3 rounded-sm bg-[#3366FF] text-white hover:bg-[#2A55D9] disabled:opacity-60 inline-flex items-center gap-1"
              >
                {sending ? "…" : <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </form>

          <div className="mt-8 flex gap-3">
            {[
              { I: Linkedin, t: "linkedin" },
              { I: Instagram, t: "instagram" },
              { I: Twitter, t: "twitter" },
            ].map(({ I, t }) => (
              <a
                key={t}
                href={`#${t}`}
                aria-label={t}
                className="w-10 h-10 rounded-sm border border-white/15 inline-flex items-center justify-center hover:border-[#6B8AFF]/60 hover:text-[#6B8AFF] transition-colors"
              >
                <I className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-4 grid grid-cols-2 gap-8">
          <div>
            <p className="overline text-[#6B8AFF] mb-5">Services</p>
            <ul className="space-y-3 text-sm text-slate-300/85">
              <li><Link to="/company-formation/mainland" className="hover:text-white">Mainland Setup</Link></li>
              <li><Link to="/company-formation/freezone" className="hover:text-white">Freezone Setup</Link></li>
              <li><Link to="/visa" className="hover:text-white">Visa & Residency</Link></li>
              <li><Link to="/banking" className="hover:text-white">Corporate Banking</Link></li>
              <li><Link to="/services/accounting" className="hover:text-white">Accounting</Link></li>
              <li><Link to="/services/corporate-tax" className="hover:text-white">Corporate Tax</Link></li>
              <li><Link to="/services/vat" className="hover:text-white">VAT</Link></li>
              <li><Link to="/services/transfer-pricing" className="hover:text-white">Transfer Pricing</Link></li>
            </ul>
          </div>
          <div>
            <p className="overline text-[#6B8AFF] mb-5">Company</p>
            <ul className="space-y-3 text-sm text-slate-300/85">
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              <li><Link to="/#faq" className="hover:text-white">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-white">Terms</Link></li>
              <li><Link to="/contact" className="hover:text-white">Privacy</Link></li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-3">
          <p className="overline text-[#6B8AFF] mb-5">Contact</p>
          <ul className="space-y-4 text-sm text-slate-300/85">
            <li className="flex gap-3">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#6B8AFF]" />
              <span>
                Sharjah Media City Free Zone
                <br /> Al Messaned, UAE
              </span>
            </li>
            <li className="flex gap-3">
              <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#6B8AFF]" />
              <span>contact@startinc.ae</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="container-x border-t border-white/10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} StartINC Corporate Services.
        </p>
        <p className="text-xs text-slate-500">
          Crafted in Sharjah · Founders' partner for UAE setup.
        </p>
      </div>
    </footer>
  );
}
